import React, { FC, useState } from 'react';
import { Text, Caption } from '@/components/atoms/AppText';
import AppToggle from '@/components/atoms/AppToggle';
import SegmentedControl from '@/components/elements/SegmentedControl';
import AppButton from '@/components/atoms/AppButton';
import {
  LuFileText,
  LuBraces,
  LuArrowLeftRight,
  LuRefreshCw,
  LuCheckCircle2,
  LuXCircle,
  LuInfo,
} from 'react-icons/lu';
import { IconBadge } from '@/components/atoms/IconBadge';
import _ from 'lodash';
import { PROVIDER_CONFIG_PROPS } from '@/constant/provider/provider';
import { APP_BG, APP_BORDER } from '@/constant/theme/colors.constant';
import { CodeViewer } from '@/components/organism/CodeViewer';
import { PROVIDER_TEST_IDS } from '@/constant/testIds/provider.testIds';
import { defaultConfigData } from './defaultConfig';
import MMSConfigSection from './sections/MMSConfigSection';
import VMSConfigSection from './sections/VMSConfigSection';
import ClientsConfigSection from './sections/ClientsConfigSection';
import AlertsConfigSection from './sections/AlertsConfigSection';
import SmartCoolersConfigSection from './sections/SmartCoolersConfigSection';
import { VDIConfigData } from './config.types';
import { tid } from '@/utils/testId';

interface ProviderConfigurationProps {
  configData: VDIConfigData;
  setConfigData: (data: VDIConfigData) => void;
  configErrors?: Record<string, string>;
}

const ProviderConfiguration: FC<ProviderConfigurationProps> = ({
  configData,
  setConfigData,
  configErrors,
}) => {
  const [openAccordion, setOpenAccordion] = useState<number>(0);
  const [configMode, setConfigMode] = useState<'form' | 'json'>('form');
  const [jsonConfigString, setJsonConfigString] = useState('');

  // Returns true if every key/nested-key in `schema` is present in `value`
  const hasAllKeys = (schema: any, value: any): boolean => {
    if (typeof schema !== 'object' || schema === null || Array.isArray(schema))
      return true;
    if (typeof value !== 'object' || value === null || Array.isArray(value))
      return false;
    for (const key of Object.keys(schema)) {
      if (!(key in value)) return false;
      if (!hasAllKeys(schema[key], value[key])) return false;
    }
    return true;
  };

  const handleAccordionToggle = (index: number) => {
    setOpenAccordion(openAccordion === index ? 0 : index);
  };

  const alertsHasError = !!(
    configErrors?.ALERTS_MMS_HOST ||
    configErrors?.ALERTS_VMS_HOST ||
    configErrors?.ALERTS_CLIENTS_HOST ||
    configErrors?.ALERTS_GATEWAY_HOST
  );
  const scHasError = !!(
    configErrors?.SC_MMS_HOST ||
    configErrors?.SC_VMS_HOST ||
    configErrors?.SC_CLIENTS_HOST ||
    configErrors?.SC_GATEWAY_HOST
  );

  return (
    <div className="mt-0 flex min-w-0 flex-col">
      <div
        className={`mb-4 flex items-center justify-end overflow-x-auto pb-3 border-b ${APP_BORDER.soft}`}
      >
        {/* Mode Switcher */}
        <SegmentedControl
          options={[
            { value: 'form', label: 'Form View', icon: LuFileText },
            { value: 'json', label: 'JSON View', icon: LuBraces },
          ]}
          value={configMode}
          testId={tid(PROVIDER_TEST_IDS.CRUD_CONFIG_MODE_SWITCHER)}
          onChange={(val) => {
            if (val === 'form' && configMode === 'json') {
              try {
                const parsed = JSON.parse(jsonConfigString);
                setConfigData(
                  _.merge({}, defaultConfigData, parsed) as VDIConfigData,
                );
              } catch (e) {
                // Invalid JSON — keep existing configData, just switch view
              }
            }
            if (val === 'json') {
              setJsonConfigString(JSON.stringify(configData, null, 2));
            }
            setConfigMode(val as 'form' | 'json');
          }}
          size="md"
          shape="pill"
          variant="gradient"
        />
      </div>

      {/* JSON Mode */}
      {configMode === 'json' ? (
        <div
          className={`border ${APP_BORDER.default} rounded-2xl overflow-hidden ${APP_BG.base} shadow-sm`}
        >
          <CodeViewer
            editable
            content={jsonConfigString}
            language="json"
            filename="config.json"
            onContentChange={(newValue) => {
              // Always update textarea so pasted JSON is visible immediately
              setJsonConfigString(newValue);
              try {
                const parsed = JSON.parse(newValue);
                // If keys are missing compared to default schema, do not update configData
                if (!hasAllKeys(defaultConfigData, parsed)) {
                  return;
                }
                // Valid JSON and all keys present — deep-merge with defaults
                const merged = _.merge(
                  {},
                  defaultConfigData,
                  parsed,
                ) as VDIConfigData;
                setConfigData(merged);
                setJsonConfigString(JSON.stringify(merged, null, 2));
              } catch {
                // Invalid JSON while typing — leave textarea as-is
              }
            }}
            footer={(() => {
              try {
                JSON.parse(jsonConfigString);
                return (
                  <div className="flex items-center gap-2 text-emerald-600 dark:text-emerald-400 text-xs font-semibold bg-emerald-50 dark:bg-emerald-950/30 px-4 py-2.5 rounded-lg border border-emerald-200 dark:border-emerald-600/30">
                    <LuCheckCircle2 className="w-4 h-4 flex-shrink-0" />
                    Valid JSON format — Ready to use
                  </div>
                );
              } catch {
                if (jsonConfigString.trim()) {
                  return (
                    <div className="flex items-center gap-2 text-red-600 dark:text-red-400 text-xs font-semibold bg-red-50 dark:bg-red-950/30 px-4 py-2.5 rounded-lg border border-red-200 dark:border-red-600/30">
                      <LuXCircle className="w-4 h-4 flex-shrink-0" />
                      Invalid JSON format — Please check syntax
                    </div>
                  );
                }
                return null;
              }
            })()}
            placeholder={PROVIDER_CONFIG_PROPS.JSON_PLACEHOLDER}
          />
          <div
            className={`${APP_BG.surface} px-5 py-4 border-t ${APP_BORDER.soft}`}
          >
            <div className="flex items-start gap-3">
              <LuInfo className="w-5 h-5 text-primary flex-shrink-0 mt-0.5" />
              <div>
                <Text as="p" size="base" weight="bold" color="default">
                  {PROVIDER_CONFIG_PROPS.PRO_TIP_TITLE}
                </Text>
                <Caption size="sm" color="muted" className="mt-0.5">
                  {PROVIDER_CONFIG_PROPS.PRO_TIP_BODY}
                </Caption>
              </div>
            </div>
          </div>
        </div>
      ) : (
        <div
          className={`border ${APP_BORDER.default} rounded-2xl overflow-hidden shadow-sm ${APP_BG.base}`}
        >
          {/* Fork Transaction Section - Match Reconciliation UI */}
          <div
            className={`p-4 sm:p-5 border-b ${APP_BORDER.soft} ${APP_BG.surface}`}
          >
            <div className="flex items-center justify-between gap-3">
              <div className="flex min-w-0 items-center gap-3">
                <IconBadge
                  icon={<LuArrowLeftRight className="w-5 h-5" />}
                  size="md"
                  variant="solid"
                  shadow={false}
                />
                <div>
                  <Text
                    as="p"
                    size="2xl"
                    weight="bold"
                    color="default"
                    className="drop-shadow-sm"
                  >
                    Fork Transaction
                  </Text>
                  <Caption size="base" color="muted" className="mt-0.5">
                    Enable forking of transaction processes across services
                  </Caption>
                </div>
              </div>
              <AppToggle
                checked
                onChange={() => { }}
                disabled
                color="primary"
                data-testid={tid(PROVIDER_TEST_IDS.CRUD_FORK_TRANSACTION_TOGGLE)}
              />
            </div>
          </div>

          {/* MMS Configuration */}
          <MMSConfigSection
            config={configData.VDI_SETTINGS.MMS}
            isOpen={openAccordion === 1}
            onToggle={() => handleAccordionToggle(1)}
            onChange={(mms) =>
              setConfigData({
                ...configData,
                VDI_SETTINGS: { ...configData.VDI_SETTINGS, MMS: mms },
              })
            }
            error={configErrors?.MMS}
          />

          {/* VMS Configuration */}
          <VMSConfigSection
            config={configData.VDI_SETTINGS.VMS}
            isOpen={openAccordion === 2}
            onToggle={() => handleAccordionToggle(2)}
            onChange={(vms) =>
              setConfigData({
                ...configData,
                VDI_SETTINGS: { ...configData.VDI_SETTINGS, VMS: vms },
              })
            }
            error={configErrors?.VMS}
          />

          {/* CLIENTS Configuration */}
          <ClientsConfigSection
            config={configData.VDI_SETTINGS.CLIENTS}
            isOpen={openAccordion === 3}
            onToggle={() => handleAccordionToggle(3)}
            onChange={(clients) =>
              setConfigData({
                ...configData,
                VDI_SETTINGS: { ...configData.VDI_SETTINGS, CLIENTS: clients },
              })
            }
            error={configErrors?.CLIENTS}
          />

          {/* ALERTS Configuration */}
          <AlertsConfigSection
            config={configData.VDI_SETTINGS.ALERTS}
            isOpen={openAccordion === 4}
            onToggle={() => handleAccordionToggle(4)}
            onChange={(alerts) =>
              setConfigData({
                ...configData,
                VDI_SETTINGS: { ...configData.VDI_SETTINGS, ALERTS: alerts },
              })
            }
            hasError={alertsHasError}
          />

          {/* SMART COOLERS Configuration */}
          <SmartCoolersConfigSection
            config={configData.VDI_SETTINGS.SMART_COOLERS}
            isOpen={openAccordion === 5}
            onToggle={() => handleAccordionToggle(5)}
            onChange={(smartCoolers) =>
              setConfigData({
                ...configData,
                VDI_SETTINGS: {
                  ...configData.VDI_SETTINGS,
                  SMART_COOLERS: smartCoolers,
                },
              })
            }
            hasError={scHasError}
          />

          {/* Reconciliation Section */}
          <div
            className={`p-4 sm:p-5 border-b ${APP_BORDER.soft} ${APP_BG.surface}`}
          >
            <div className="flex items-center justify-between gap-3">
              <div className="flex min-w-0 items-center gap-3">
                <IconBadge
                  icon={<LuRefreshCw className="w-5 h-5" />}
                  size="md"
                  variant="solid"
                  shadow={false}
                />
                <div>
                  <Text
                    as="p"
                    size="2xl"
                    weight="bold"
                    color="default"
                    className="drop-shadow-sm"
                  >
                    Reconciliation
                  </Text>
                  <Caption size="base" color="muted" className="mt-0.5">
                    Enable reconciliation processing
                  </Caption>
                </div>
              </div>
              <AppToggle
                checked={configData.RECONCILIATION.ENABLED}
                onChange={(checked) =>
                  setConfigData({
                    ...configData,
                    RECONCILIATION: { ENABLED: checked },
                  })
                }
                color="primary"
                data-testid={tid(PROVIDER_TEST_IDS.CRUD_RECONCILIATION_TOGGLE)}
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ProviderConfiguration;
